import { z } from 'zod';
import { DomainSchema, type Domain } from './enums';
import {
  FrameworkIdSchema,
  FRAMEWORK_POLICY,
  frameworkPermits,
  machineKeyPrefix,
  type FrameworkId,
} from './framework-policy';
import { QrsControlIdSchema } from './ids';
import { NonEmptyStringSchema, SemVerSchema } from './primitives';

/**
 * The QRS control catalogue and its framework mapping layer
 * (docs/DECISIONS.md ZD-032, ZD-034, ZD-035).
 *
 * **The dependency runs one way.** A threat pattern references a QRS control
 * id. The catalogue maps outward to ISO, NIST, CAF and Cyber Essentials. No
 * pattern ever names an external framework identifier, which is what keeps the
 * licensing question in one file (`./framework-policy`) instead of spread
 * across every pack.
 *
 * **And the granularity is why the layer exists at all,** not merely the
 * licensing. ISO 27002 and NIST CSF are governance controls. What a threat
 * pattern points at is an engineering instruction — *terminate TLS at the trust
 * boundary*, *constrain agent tool permissions* — and no published framework
 * operates there. Mapping a pattern straight onto A.8.24 keeps the category and
 * loses the instruction.
 *
 * The catalogue **content** is human-authored markdown under
 * `packages/packs/catalogue/`, parsed into this shape and committed as a
 * generated module (ZD-035 §1). What is here is the shape it must satisfy.
 */

/**
 * How exact a mapping from a QRS control to a framework control is
 * (docs/DECISIONS.md ZD-034 for what each one means).
 *
 * Four values rather than a boolean, because a mapping is a *claim about
 * correspondence* and the claim is usually not exact. Collapsing "this is the
 * control" and "a reviewer should check whether this is the control" into one
 * value would put both into a client's evidence pack looking identical, which
 * is the kind of quiet over-claim CLAUDE.md principle 10 is about.
 */
export const MAPPING_FIDELITIES = [
  /**
   * Bidirectional sufficiency: implementing the QRS control satisfies the
   * framework item on its own, **and** holding the framework item delivers the
   * QRS control on its own. One caveat denies it (ZD-034).
   */
  'direct',
  /** The framework control covers part of what the QRS control requires. */
  'partial',
  /** Related, and worth citing, but not a control-to-control correspondence. */
  'advisory',
  /** Defensible only for some architectures; a human decides per assessment. */
  'requires_reviewer_confirmation',
] as const;
export const MappingFidelitySchema = z.enum(MAPPING_FIDELITIES);
export type MappingFidelity = z.infer<typeof MappingFidelitySchema>;

const FrameworkMappingRowShape = z.object({
  qrs_control_id: QrsControlIdSchema,
  framework: FrameworkIdSchema,
  /**
   * **Required.** "ISO 27001" without a year names two different documents
   * whose control sets differ, and a mapping to the wrong one is wrong in a way
   * nobody can see. The same reasoning as pinning a pack version (§12.2): a
   * reference with no version is not reproducible.
   */
  framework_version: NonEmptyStringSchema,
  control_id: NonEmptyStringSchema,
  /**
   * Nullable, and the null is load-bearing (ZD-019).
   *
   * For an `identifier`-only framework there is nothing we may write here, and
   * a required field would force an author to invent a title that reads in a
   * report exactly like the framework's own. Where a name does appear beside an
   * ISO or CE identifier in the source markdown it is a reader aid, and the
   * importer drops it in an explicit branch (ZD-035 §4.3).
   */
  control_name: NonEmptyStringSchema.nullable(),
  fidelity: MappingFidelitySchema,
  /** Why this correspondence holds — read by the reviewer, not by any code. */
  note: z.string(),
});

export type FrameworkMappingRow = z.infer<typeof FrameworkMappingRowShape>;

/**
 * The licensing control, applied at the parse (ZD-032, ZD-035).
 *
 * A rejected framework cannot be named at all; a title cannot be carried for a
 * framework whose policy permits identifiers only; and a framework whose
 * identifiers we have not cleared cannot be keyed by its own identifier scheme.
 * All three are refused here rather than reviewed later, because "we agreed not
 * to" is not a control — the same posture as `quarantine.test.ts` asserting
 * that apps cannot import the corpus.
 */
export const FrameworkMappingRowSchema = FrameworkMappingRowShape.superRefine((row, ctx) => {
  const policy = FRAMEWORK_POLICY[row.framework];
  if (policy.status !== 'permitted') {
    ctx.addIssue({
      code: 'custom',
      message: `${policy.display_name} is a rejected framework (${policy.licence}): ${policy.rationale}`,
      path: ['framework'],
    });
    return;
  }
  if (row.control_name !== null && !frameworkPermits(row.framework, 'identifier_and_title')) {
    ctx.addIssue({
      code: 'custom',
      message:
        `${policy.display_name} permits ${String(policy.permits)} only, so a mapping row may ` +
        `not carry its control title — put a QRS-authored name on the QRS control instead`,
      path: ['control_name'],
    });
  }
  if (policy.identifier_source === 'qrs_machine_key') {
    const prefix = machineKeyPrefix(row.framework);
    if (!row.control_id.startsWith(prefix)) {
      ctx.addIssue({
        code: 'custom',
        message:
          `${policy.display_name} is keyed by QRS-side machine keys, so a mapping row's ` +
          `control_id must start with "${prefix}" — carrying the framework's own identifier ` +
          `would reproduce a scheme its licence does not clear (${policy.licence})`,
        path: ['control_id'],
      });
    }
  }
});

/**
 * A framework/control cell the catalogue deliberately leaves unmapped
 * (docs/DECISIONS.md ZD-035 §2).
 *
 * **The reason this is a record rather than an absence.** *"Cyber Essentials
 * has no encryption-in-transit requirement"* is an assertion — it is what the
 * CE scope-clarification artefact rests on, and what the sds-05 capability of
 * telling a certified client where their certificate stops is built from. If a
 * considered decision and a forgotten row have the same shape, nothing can
 * distinguish them, and a forgotten row rendered to a client as a considered
 * decision is precisely the quiet over-claim CLAUDE.md principle 10 forbids.
 *
 * The source markdown proved the point before the schema existed: its first
 * draft stated 32 unmapped cells and carried 26 reasons, with the gap
 * unnoticed until this schema's completeness check was run over it (ZD-035
 * §2). The published catalogue declares all 32.
 */
export const UnmappedCellSchema = z.object({
  framework: FrameworkIdSchema,
  framework_version: NonEmptyStringSchema,
  /** Why this framework has nothing genuine to offer this control. */
  reason: NonEmptyStringSchema,
});
export type UnmappedCell = z.infer<typeof UnmappedCellSchema>;

/**
 * Text a control's generated output must carry verbatim, where the control's
 * protection is partial or contested.
 *
 * sds-05 turns a control into a recommendation, and a recommendation that
 * reads as a flat claim of prevention over a control that only reduces
 * likelihood is an over-claim the client acts on. The commercially expensive
 * case is pseudonymisation: *"pseudonymised data is out of GDPR scope"* is a
 * common and costly client misconception, and correcting it is part of what
 * makes a report worth paying for.
 *
 * **Verbatim is asserted, not requested.** `controlOutputLines` returns the
 * caveat by identity, and the generated-catalogue test compares it
 * byte-for-byte with the source sentence — a softened paraphrase fails both.
 */
export const EfficacyCaveatSchema = NonEmptyStringSchema;

/**
 * A control that encodes a legal obligation, with the citation
 * (docs/DECISIONS.md ZD-035).
 *
 * Marked so that report framing is **generated from the presence of such a
 * control** rather than carried as a static disclaimer whether or not any
 * apply. A disclaimer that appears on every report is one nobody reads; one
 * that appears because Article 6 is genuinely in play is a different document.
 */
export const LegalObligationSchema = z.object({
  /** The instrument, e.g. `UK GDPR`. */
  instrument: NonEmptyStringSchema,
  /** Article-level citations, e.g. `Article 6`, `Article 9`. */
  citations: z.array(NonEmptyStringSchema).min(1),
});
export type LegalObligation = z.infer<typeof LegalObligationSchema>;

/**
 * One control in the QRS catalogue: a mitigation stated at the granularity a
 * pattern actually points at.
 */
const QrsControlShape = z.object({
  qrs_control_id: QrsControlIdSchema,
  /**
   * What the control requires, in one sentence, phrased as an instruction. This
   * is the thing a recommendation is generated from in sds-05, so it has to be
   * actionable on its own rather than a category name.
   *
   * There is no separate `title`. The field existed, was required, was read by
   * nothing, and the source supplies no short handle distinct from this
   * sentence — ZD-019's question with no honest answer (ZD-035 §4.2).
   */
  statement: NonEmptyStringSchema,
  /** What the control is and what it prevents, and what "implemented" means. */
  description: NonEmptyStringSchema,
  /**
   * Scaled implementation guidance, both tiers client-facing input to sds-05.
   *
   * Two tiers rather than one because the achievable baseline for a five-person
   * company and the right answer for a regulated estate are different
   * instructions, and a recommendation that gives the second to the first is
   * one the client cannot act on.
   */
  implementation: z.object({
    floor: NonEmptyStringSchema,
    at_scale: NonEmptyStringSchema,
  }),
  /**
   * What demonstrates the control is in place. One sentence, as the source
   * states it — splitting it on its semicolons would invent structure the
   * author did not write (ZD-021's mirror: carry what was said, in the shape it
   * was said).
   */
  evidence: NonEmptyStringSchema,
  /** Domains whose patterns may reference it. Cross-domain controls list several. */
  domains: z.array(DomainSchema).min(1),
  /** Verbatim caveat where the control's protection is partial or contested. */
  efficacy_caveat: EfficacyCaveatSchema.nullable(),
  /** Set where the control encodes a legal obligation rather than good practice. */
  legal_obligation: LegalObligationSchema.nullable(),
  framework_mappings: z.array(FrameworkMappingRowSchema),
  /** Cells this control deliberately leaves unmapped, each with its reason. */
  unmapped: z.array(UnmappedCellSchema),
  /** Reviewer-facing commentary from the source that is not one of the above. */
  review_notes: z.array(NonEmptyStringSchema),
});
export type QrsControl = z.infer<typeof QrsControlShape>;

export const QrsControlSchema = QrsControlShape.superRefine((control, ctx) => {
  /* Every mapping row on a control must be about that control. */
  control.framework_mappings.forEach((row, i) => {
    if (row.qrs_control_id !== control.qrs_control_id) {
      ctx.addIssue({
        code: 'custom',
        message: `mapping row names ${row.qrs_control_id}, but it sits on ${control.qrs_control_id}`,
        path: ['framework_mappings', i, 'qrs_control_id'],
      });
    }
  });

  /*
   * A framework is mapped or unmapped, never both. Both would be a cell that
   * says "here is the correspondence" and "there is no correspondence" at once,
   * and whichever a reader saw first would be the one they believed.
   */
  const mapped = new Set(control.framework_mappings.map((row) => row.framework));
  control.unmapped.forEach((cell, i) => {
    if (mapped.has(cell.framework)) {
      ctx.addIssue({
        code: 'custom',
        message:
          `${FRAMEWORK_POLICY[cell.framework].display_name} is both mapped and declared ` +
          `unmapped on ${control.qrs_control_id}`,
        path: ['unmapped', i, 'framework'],
      });
    }
    if (FRAMEWORK_POLICY[cell.framework].status !== 'permitted') {
      ctx.addIssue({
        code: 'custom',
        message:
          `${FRAMEWORK_POLICY[cell.framework].display_name} is a rejected framework, so ` +
          `declaring it unmapped states a scope decision about a framework we may not name`,
        path: ['unmapped', i, 'framework'],
      });
    }
  });
});

/**
 * Which frameworks this catalogue asserts coverage over, and how far
 * (docs/DECISIONS.md ZD-035 §2).
 *
 * This is what makes "unmapped" mean something. Without it, a framework the
 * catalogue never intended to map and a framework it forgot to map are the same
 * absence.
 */
export const FRAMEWORK_COVERAGES = [
  /**
   * Every in-scope control carries a row or an `unmapped` declaration. A bare
   * absence is a parse error.
   */
  'exhaustive',
  /**
   * A row exists only where it clears a stated bar, so absence carries no
   * meaning. The AI 600-1 enrichment pass is the only instance: a row exists
   * only where a suggested action lifts a control's AI-framework fidelity.
   */
  'targeted',
] as const;
export const FrameworkCoverageSchema = z.enum(FRAMEWORK_COVERAGES);
export type FrameworkCoverage = z.infer<typeof FrameworkCoverageSchema>;

export const FrameworkScopeSchema = z.object({
  framework: FrameworkIdSchema,
  /** The edition this catalogue mapped against. */
  framework_version: NonEmptyStringSchema,
  coverage: FrameworkCoverageSchema,
  /**
   * Restricts the controls the framework is in scope for. `null` means every
   * control — a framework scoped to `['ai']` is in scope only for controls
   * whose `domains` include it.
   */
  applies_to_domains: z.array(DomainSchema).min(1).nullable(),
});
export type FrameworkScope = z.infer<typeof FrameworkScopeSchema>;

const ControlCatalogueShape = z.object({
  /**
   * Semantic version with a defined meaning (ZD-035 §6): **major** for a
   * control removed or its meaning changed under an unchanged id, **minor** for
   * controls added, **patch** for mapping rows and prose corrected. A rename is
   * a removal and an addition, never a patch, because a `qrsctl_*` id is a
   * permanent anchor (ZD-004) and the anchor *is* the identity (ZD-028).
   */
  catalogue_version: SemVerSchema,
  title: NonEmptyStringSchema,
  description: z.string(),
  frameworks_in_scope: z.array(FrameworkScopeSchema),
  controls: z.array(QrsControlSchema),
});
export type ControlCatalogue = z.infer<typeof ControlCatalogueShape>;

/** True where `framework` is in scope for a control carrying `domains`. */
function scopeCovers(scope: FrameworkScope, domains: readonly Domain[]): boolean {
  if (scope.applies_to_domains === null) return true;
  return scope.applies_to_domains.some((domain) => domains.includes(domain));
}

export const ControlCatalogueSchema = ControlCatalogueShape.superRefine((catalogue, ctx) => {
  const seen = new Set<string>();
  catalogue.controls.forEach((control, i) => {
    if (seen.has(control.qrs_control_id)) {
      ctx.addIssue({
        code: 'custom',
        message: `duplicate qrs_control_id ${control.qrs_control_id}`,
        path: ['controls', i, 'qrs_control_id'],
      });
    }
    seen.add(control.qrs_control_id);
  });

  const scopes = new Map(catalogue.frameworks_in_scope.map((scope) => [scope.framework, scope]));

  /*
   * A row or a declaration may only name a framework the catalogue claims to
   * cover. Otherwise the charter says nothing about the rows that exist, and a
   * framework could be mapped on one control and out of scope on the next.
   */
  catalogue.controls.forEach((control, i) => {
    for (const row of control.framework_mappings) {
      const scope = scopes.get(row.framework);
      if (scope === undefined) {
        ctx.addIssue({
          code: 'custom',
          message:
            `${control.qrs_control_id} maps ${FRAMEWORK_POLICY[row.framework].display_name}, ` +
            `which is not in frameworks_in_scope`,
          path: ['controls', i, 'framework_mappings'],
        });
      } else if (row.framework_version !== scope.framework_version) {
        ctx.addIssue({
          code: 'custom',
          message:
            `${control.qrs_control_id} maps ${FRAMEWORK_POLICY[row.framework].display_name} ` +
            `${row.framework_version}, but the catalogue is scoped to ${scope.framework_version}`,
          path: ['controls', i, 'framework_mappings'],
        });
      }
    }
    for (const cell of control.unmapped) {
      if (!scopes.has(cell.framework)) {
        ctx.addIssue({
          code: 'custom',
          message:
            `${control.qrs_control_id} declares ` +
            `${FRAMEWORK_POLICY[cell.framework].display_name} unmapped, but it is not in ` +
            `frameworks_in_scope`,
          path: ['controls', i, 'unmapped'],
        });
      }
    }
  });

  /*
   * Completeness, unconditional: under an `exhaustive` charter every in-scope
   * cell is either a mapping row or a declared reason. Nothing else is a legal
   * state — which is the whole of ZD-035 §2 in one loop. There is no register
   * for a cell that is neither: an undeclared cell is a parse error, full stop,
   * because the catalogue this schema validates is expected to have none.
   */
  for (const scope of catalogue.frameworks_in_scope) {
    if (scope.coverage !== 'exhaustive') continue;
    catalogue.controls.forEach((control, i) => {
      if (!scopeCovers(scope, control.domains)) return;
      const mapped = control.framework_mappings.some((row) => row.framework === scope.framework);
      const declared = control.unmapped.some((cell) => cell.framework === scope.framework);
      if (mapped || declared) return;
      ctx.addIssue({
        code: 'custom',
        message:
          `${control.qrs_control_id} has neither a ` +
          `${FRAMEWORK_POLICY[scope.framework].display_name} mapping row nor an unmapped ` +
          `declaration, and ${FRAMEWORK_POLICY[scope.framework].display_name} coverage is ` +
          `exhaustive — an absence and a decision must not be the same shape`,
        path: ['controls', i, 'unmapped'],
      });
    });
  }
});

export type ControlCatalogueIndex = ReadonlyMap<string, QrsControl>;

export function indexControlCatalogue(catalogue: ControlCatalogue): ControlCatalogueIndex {
  return new Map(catalogue.controls.map((control) => [control.qrs_control_id, control]));
}

/**
 * The attribution lines a report must carry, given the frameworks it cited.
 *
 * Computed from the policy rather than written into a template, so a new
 * framework cannot reach a client's report without its attribution coming with
 * it.
 */
export function attributionsFor(frameworks: readonly FrameworkId[]): string[] {
  return [
    ...new Set(
      frameworks
        .map((id) => FRAMEWORK_POLICY[id].attribution)
        .filter((line): line is string => line !== null),
    ),
  ].sort();
}

export interface SupersededMapping {
  qrs_control_id: string;
  framework: FrameworkId;
  /** What the row is pinned to. */
  mapped_version: string;
  /** What the policy says is current, or `null` where nobody has established it. */
  current_version: string | null;
}

/**
 * Mapping rows pinned to an edition that is no longer the current one
 * (docs/DECISIONS.md ZD-035 §4).
 *
 * Cyber Essentials revises annually, so without this a mapping quietly becomes
 * a claim about a superseded document while continuing to read as a claim about
 * the client's certificate.
 *
 * **A framework whose `current_version` is null is reported, not passed.** That
 * is the conservative direction: "we have not established what is current" and
 * "this row is current" are different statements, and only one of them is safe
 * to render beside a client's certification.
 */
export function supersededMappings(catalogue: ControlCatalogue): SupersededMapping[] {
  const superseded: SupersededMapping[] = [];
  for (const control of catalogue.controls) {
    for (const row of control.framework_mappings) {
      const current = FRAMEWORK_POLICY[row.framework].current_version;
      if (current !== null && current === row.framework_version) continue;
      superseded.push({
        qrs_control_id: control.qrs_control_id,
        framework: row.framework,
        mapped_version: row.framework_version,
        current_version: current,
      });
    }
  }
  return superseded;
}

/**
 * Controls the catalogue defines that no pattern references.
 *
 * A warning rather than a failure: a control ahead of its pattern is a
 * reasonable state for a catalogue to be in, and sds-04a labels expected
 * findings against the patterns that exist. It is worth knowing before that
 * session, because a control nothing points at generates no recommendation and
 * so is invisible in every output the client sees.
 */
export function orphanControls(
  catalogue: ControlCatalogue,
  referenced: readonly string[],
): string[] {
  const wanted = new Set(referenced);
  return catalogue.controls
    .map((control) => control.qrs_control_id)
    .filter((id) => !wanted.has(id))
    .sort();
}

/**
 * The lines any client-facing rendering of a control must carry.
 *
 * **This is the seam the efficacy caveat is asserted against.** sds-05 does not
 * exist yet, so "the caveat must survive generation verbatim" had nowhere to be
 * checked; putting the rule in a comment would have made it a convention, which
 * this codebase has repeatedly decided is not a rule at all. Rendering through
 * here means the caveat is returned **by identity** and a test can say so.
 *
 * It is deliberately thin. It is not a report template and must not grow into
 * one — its job is to be the single place a control becomes client-facing text,
 * so that the things that must not be dropped cannot be dropped.
 */
export function controlOutputLines(control: QrsControl): string[] {
  const lines = [control.statement];
  if (control.efficacy_caveat !== null) lines.push(control.efficacy_caveat);
  if (control.legal_obligation !== null) {
    const { instrument, citations } = control.legal_obligation;
    lines.push(
      `${instrument} ${citations.join('; ')} applies to this control; findings state what the ` +
        `design evidences against it, never whether the organisation complies.`,
    );
  }
  return lines;
}

/**
 * A control's implementation guidance, as the two tiers the catalogue authored
 * plus the evidence sentence (docs/DECISIONS.md ZD-035 §4.1, ZD-060).
 *
 * **Separate from `controlOutputLines` rather than folded into it.** That seam
 * exists so the efficacy caveat and the legal-obligation framing cannot be
 * dropped, and its docstring says in terms that it must not grow into a report
 * template. These three fields carry neither of those things, so putting them
 * through it would widen the one function whose narrowness is the point.
 *
 * **Both tiers, and no selection.** ZD-035 §4.1 authored two because the
 * achievable baseline for a five-person company and the right answer for a
 * regulated estate are different instructions. Nothing in an assessment says
 * which a client is: `System.internet_facing` and `multi_tenant` are `unknown`
 * on 4 of 4 corpus systems, no environment is established on any of them, and
 * `Tenant.tier` is a commercial plan rather than a size. A component count is
 * the only scale-shaped number available and it disqualifies itself —
 * brackenwell and culverhay both have 7 components and produce 2 findings
 * against 27, because that number measures how much detail the documents
 * carried. So both tiers ship, labelled, and the report says the choice is the
 * client's (ZD-060 §2).
 */
export const ControlImplementationSchema = z.object({
  floor: NonEmptyStringSchema,
  at_scale: NonEmptyStringSchema,
  evidence: NonEmptyStringSchema,
});
export type ControlImplementation = z.infer<typeof ControlImplementationSchema>;

/**
 * A `qrsctl_*` id inside the catalogue's own prose, always inside backticks.
 *
 * Measured over all 36 controls before this existed: **12 references across 9
 * controls, every one backtick-enclosed, every one resolving** — 6 in
 * `description`, 4 in `implementation.floor`, 2 in `implementation.at_scale`,
 * and **0 in `statement` or `evidence`**. A uniform syntax is what lets the
 * substitution below be total rather than a heuristic over prose.
 */
const CONTROL_REFERENCE = /`(qrsctl_[a-z0-9_.]+)`/g;

export class UnresolvedControlReferenceError extends Error {
  constructor(
    readonly controlId: string,
    readonly referenced: string,
  ) {
    super(
      `${controlId}'s implementation guidance references ${referenced}, which is not in the ` +
        `catalogue. A client-facing sentence cannot carry an identifier that resolves to nothing`,
    );
    this.name = 'UnresolvedControlReferenceError';
  }
}

export class BareControlReferenceError extends Error {
  constructor(
    readonly controlId: string,
    readonly text: string,
  ) {
    super(
      `${controlId}'s implementation guidance carries a qrsctl_ id outside backticks, so the ` +
        `resolver cannot see it and an internal identifier would reach a client: "${text}"`,
    );
    this.name = 'BareControlReferenceError';
  }
}

/**
 * The catalogue's cross-references, resolved into the sentence they meant.
 *
 * **This is why the implementation tiers could not simply be printed.** Six of
 * them name a companion control by its `qrsctl_*` id — correct for a reviewer of
 * the markdown, and an internal identifier in front of a client. All five
 * controls carrying one are cited on the corpus, so this is live content rather
 * than a hypothetical, and `report.test.ts`'s `INTERNAL_ONLY` list names
 * `qrsctl_` for exactly this reason. It is `sds-07a`'s pack-ids-mid-sentence
 * defect in a different field, caught **before** a baseline pinned it rather
 * than after (ZD-060 §3).
 *
 * Three routes were available and two are closed. Shipping as-is publishes our
 * vocabulary. Re-authoring the six sentences is a catalogue **content** change,
 * and the reference is genuinely useful in the reviewable source. So the id is
 * resolved at read time into the referenced control's own statement — the same
 * posture ZD-032 and ZD-059 take for framework mappings, where the id is the
 * internal anchor and the client sees the resolved thing.
 *
 * **It reads better rather than merely safer**, which is the argument for it
 * over an exclusion: *"compensated by `qrsctl_access.mfa_for_administrative_
 * access`"* becomes *"compensated by 'Require multi-factor authentication on
 * every administrative surface'"*, which is what the sentence always meant.
 *
 * The trailing full stop comes off — every one of the 11 referenced statements
 * ends in one, and they are being inserted mid-sentence.
 */
export function resolveControlReferences(
  control: QrsControl,
  index: ControlCatalogueIndex,
  text: string,
): string {
  const resolved = text.replace(CONTROL_REFERENCE, (_match, referenced: string) => {
    const target = index.get(referenced);
    if (!target) throw new UnresolvedControlReferenceError(control.qrs_control_id, referenced);
    return `“${target.statement.replace(/\.$/, '')}”`;
  });

  /*
   * A bare id defeats the substitution silently, which is the failure this
   * whole function exists to prevent — so it is refused rather than passed
   * through. Zero occur today; the check is here because "every one is
   * backticked" is a fact about the catalogue as it stands, not a property the
   * markdown enforces.
   */
  if (resolved.includes('qrsctl_')) {
    throw new BareControlReferenceError(control.qrs_control_id, resolved);
  }
  return resolved;
}

/**
 * The implementation guidance a client-facing rendering carries, with the
 * catalogue's cross-references resolved.
 *
 * `description` is **not** here, and the exclusion is measured rather than
 * stylistic: `refuseLegalConclusion` fires on 2 of 36 descriptions —
 * `qrsctl_data.processor_agreement` (*"prevents unlawful processor
 * relationships"*) and `qrsctl_data.retention_limit` (*"breaches the
 * storage-limitation principle"*). Both are correct catalogue prose about what
 * the control prevents, so both are false positives of a phrase list that says
 * of itself that it is a floor rather than a proof — but `refuseConclusions`
 * already runs over catalogue text by design, so there is no exemption to
 * invoke, and on the corpus lifting `description` would throw on 10 rows across
 * 10 findings on three of four systems. Re-authoring those two sentences is a
 * catalogue change, scheduled rather than taken here (ZD-060 §5).
 *
 * The other four fields are clean: 0 hits across all 36 on `statement`,
 * `floor`, `at_scale` and `evidence`.
 */
export function controlImplementationLines(
  control: QrsControl,
  index: ControlCatalogueIndex,
): ControlImplementation {
  return ControlImplementationSchema.parse({
    floor: resolveControlReferences(control, index, control.implementation.floor),
    at_scale: resolveControlReferences(control, index, control.implementation.at_scale),
    evidence: resolveControlReferences(control, index, control.evidence),
  });
}

/**
 * Phrasings that state a legal **conclusion about the organisation** rather
 * than an observation about the design (docs/DECISIONS.md ZD-035).
 *
 * The discipline: a finding says *"the design does not evidence a recorded
 * lawful basis for this processing, which UK GDPR Article 6 requires"* — a
 * factual observation about an artefact. It never says *"you are
 * non-compliant"*, which is a conclusion QRS is not positioned to reach.
 *
 * **This list is a floor, not a proof, and saying so is part of the control.**
 * A phrase list catches the known forms; a model or an author can reach a
 * conclusion in wording nobody enumerated. It exists because the alternative —
 * the rule living only in prose — is the failure mode this codebase keeps
 * finding, not because it makes review unnecessary.
 */
export const LEGAL_CONCLUSION_PHRASES = [
  'non-compliant',
  'noncompliant',
  'not compliant',
  'is compliant',
  'are compliant',
  'you are compliant',
  'in breach',
  'breaches the',
  'violates',
  'violation of',
  'unlawful',
  'illegal',
  'fails to comply',
  'does not comply',
] as const;

/**
 * Refuses text that states a legal conclusion. Returns the phrases found —
 * empty means the text is a design observation.
 */
export function refuseLegalConclusion(text: string): string[] {
  const haystack = text.toLowerCase();
  return LEGAL_CONCLUSION_PHRASES.filter((phrase) => haystack.includes(phrase));
}

/**
 * The report-level framing line, generated **only** where a marked control is
 * present.
 *
 * `null` when none is, which is the point: a static disclaimer carried whether
 * or not any legal obligation is in play is one nobody reads, and it also
 * over-claims in the other direction by implying a legal reading of a report
 * that contains none.
 */
export function legalObligationFraming(controls: readonly QrsControl[]): string | null {
  const instruments = [
    ...new Set(
      controls
        .map((control) => control.legal_obligation?.instrument)
        .filter((instrument): instrument is string => instrument !== undefined),
    ),
  ].sort();
  if (instruments.length === 0) return null;
  return (
    `Some controls in this report encode obligations under ${instruments.join(' and ')}. ` +
    `Findings state what the design evidences against a cited obligation. They are ` +
    `observations about the design, not a legal assessment of the organisation, and they ` +
    `do not constitute legal advice.`
  );
}

/**
 * The empty catalogue.
 *
 * Retained as a fixture for schema-level tests that need a well-formed
 * catalogue with no content. **The real catalogue lives in `@zevik/packs`**
 * (ZD-035 §1): it is generated from human-authored markdown, and it is there
 * rather than here both because ZD-032's confidentiality argument makes the
 * private workspace package the right container, and because `QrsControl.domains`
 * is derived from which patterns reference each control — which a package the
 * packs depend on cannot see.
 */
export const EMPTY_CONTROL_CATALOGUE: ControlCatalogue = {
  catalogue_version: '0.0.0',
  title: 'QRS control catalogue',
  description: 'An empty catalogue. The real one is generated in @zevik/packs.',
  frameworks_in_scope: [],
  controls: [],
};
